
import { IDraft } from "../interfaces/IDraft";
import { INavigatorResponse } from '../interfaces/INavigatorResponse';
import { Environment } from './Environment';
import { LocalizationService } from './LocalizationService';
import { FetchProxy } from 'intraactive-sdk-helper';
import { sdkContainerInstance } from '../global/Container';

export default class NavigatorService {

  // The "add image" button in the admin panel intentionally adds an empty slot so the user
  // can then pick an image for it - strip those out here (save time) rather than on every
  // edit, so the empty slot doesn't disappear from the UI before an image is chosen.
  private static stripEmptyImageUrls(rawJson: string): string {
    if (!rawJson) {
      return rawJson;
    }
    try {
      const navigator = JSON.parse(rawJson);
      const stripFromElements = (elements: any[]) => {
        elements?.forEach((element: any) => {
          if (Array.isArray(element?.config?.imageUrls)) {
            element.config.imageUrls = element.config.imageUrls.filter((url: string) => !!url);
          }
        });
      };
      stripFromElements(navigator?.smartmenu?.elements);
      stripFromElements(navigator?.megamenu?.topItems);
      return JSON.stringify(navigator);
    } catch (error) {
      console.log(error);
      return rawJson;
    }
  }

  public static isCheckedOut(url: string, token: string, instance: string): Promise<any> {
    console.log(instance);
    return new Promise((resolve) => {
      if (url && token) {
        return new FetchProxy(url, {
          credentials: "same-origin",
          mode: "cors",
          headers: {
            "AuthToken": token
          }
        })
        .withContainer(sdkContainerInstance)
        .fetch()
        .then(function (response) {
          if (!response.ok) {
            return response.status;
          } else {
            return response.json();
          }
        }).then(response => {
          console.log(response);
          resolve(response);
        }).catch((error) => {
          console.log(error);
        });
      } else {
        resolve(null);
      }
    });
  }

  public static checkOut(url: string, token: string, instance: string): Promise<any> {
    console.log(instance);
    return new Promise((resolve) => {
      if (url && token) {
        return new FetchProxy(url, {
          method: "PUT",
          credentials: "same-origin",
          mode: "cors",
          headers: {
            "AuthToken": token,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        })
        .withContainer(sdkContainerInstance)
        .fetch()
        .then(function (response) {
          if (!response.ok) {
            return response.status;
          } else {
            resolve(response);
          }
        }).catch((error) => {
          console.log(error);
        });
      } else {
        resolve(null);
      }
    });
  }

  public static getNavigator(apiUrl, urlPath: string, token, instance: string): Promise<INavigatorResponse> {
    console.log(instance);
    return new Promise((resolve) => {
      if (apiUrl && urlPath && token) {
        return new FetchProxy(`${apiUrl}${urlPath}`, {
          credentials: "same-origin",
          mode: "cors",
          headers: {
            "AuthToken": token
          }
        })
          .whenReAuthenticates(() => {
            localStorage.removeItem("configuration");
          })
          .onKnownHttpError((message, response) => {
            let errorType;
            switch (response.status) {
              case 401: // Authentication fails
                errorType = new LocalizationService().strings.HttpError_401
                break;
              case 403: // Forbidden (Permissions)
                errorType = new LocalizationService().strings.HttpError_403
                break;
              case 404: // Not found
                errorType = new LocalizationService().strings.HttpError_404
                break;
              case 503: // Busy
                errorType = new LocalizationService().strings.HttpError_503
                break;
              case 500: // Unknown exception
                errorType = new LocalizationService().strings.HttpError_500
                break;
              default:
                errorType = message;
                break;
            }
            resolve({
              navigator: undefined,
              httpStatus: {
                statusCode: response.status,
                message: "Navigator: " + errorType
              }
            });
          })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then(function (response) {
            return response.json();
          }).then(response => {
            console.log(response);
            resolve({
              navigator: response,
              httpStatus: {
                statusCode: response.status,
                message: "Navigator: " + response.statusText
              }
            });
          }).catch((error) => {
            console.log(error);
            resolve({
              navigator: undefined,
              httpStatus: {
                statusCode: 500,
                message: "Navigator: " + error.message != undefined ? error.message : error
              }
            });
          });
      } else {
        resolve(null);
      }
    });
  }

  public static updateNavigator(url: string, instanceId: number, token: string, instance: string): Promise<number> {
    console.log(instance);
    return new Promise((resolve) => {
      if (url && instanceId && token) {
        const body = NavigatorService.stripEmptyImageUrls(localStorage.getItem(`navigator-${instanceId}`));
        return new FetchProxy(url, {
          method: "PUT",
          credentials: "same-origin",
          mode: "cors",
          headers: {
            "AuthToken": token,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: body
        })
        .withContainer(sdkContainerInstance)
        .fetch()
        .then(function (response) {
          if (!response.ok) {
            throw (response.status);
          } else {
            return response.json();
          }
        }).then(response => {
          console.log(response);
          resolve(response);
        }).catch((error) => {
          console.log(error);
          resolve(undefined);
        });
      } else {
        resolve(undefined);
      }
    });
  }

  public static deleteNavigator(url: string, instanceId: number, token: string, instance: string): Promise<void> {
    console.log(instance);
    return new Promise((resolve) => {
      if (url && instanceId && token) {
        return new FetchProxy(url, {
          method: "DELETE",
          credentials: "same-origin",
          mode: "cors",
          headers: {
            "AuthToken": token,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        })
        .withContainer(sdkContainerInstance)
        .fetch()
        .then(function (response) {
          if (!response.ok) {
            throw (response.status);
          } else {
            resolve();
          }
        }).catch((error) => {
          console.log(error);
        });
      } else {
        resolve(null);
      }
    });
  }

  public static createNavigator(url: string, instanceId: number, token: string, instance: string): Promise<any> {
    console.log(instance);
    return new Promise((resolve) => {
      if (url && instanceId && token) {
        return new FetchProxy(url, {
          method: "POST",
          credentials: "same-origin",
          mode: "cors",
          headers: {
            "AuthToken": token,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: NavigatorService.stripEmptyImageUrls(localStorage.getItem(`navigator-${instanceId}`))
        })
        .withContainer(sdkContainerInstance)
        .fetch()
        .then(function (response) {
          if (!response.ok) {
            throw (response.status);
          } else {
            return response.json();
          }
        }).then(response => {
          console.log(response);
          resolve(response);
        }).catch((error) => {
          console.log(error);
        });
      } else {
        resolve(null);
      }
    });
  }

  public static getAllNavigators(url: string, token: string, instance): Promise<IDraft[]> {
    console.log(instance);
    return new Promise((resolve) => {
      if (url && token) {
        return new FetchProxy(url, {
          credentials: "same-origin",
          mode: "cors",
          headers: {
            "AuthToken": token
          }
        })
        .withContainer(sdkContainerInstance)
        .fetch()
        .then(function (response) {
          if (!response.ok) {
            throw (response.status);
          } else {
            return response.json();
          }
        }).then(response => {
          console.log(response);
          resolve(response);
        }).catch((error) => {
          console.log(error);
        });
      } else {
        resolve(null);
      }
    });
  }

  public static getInstances(token: string, tenant: string, environment: "Development" | "Test" | "Production", site: string): Promise<any[]> {
    return new Promise((resolve) => {
      if (token && tenant && environment && site) {
        const url = `${Environment.getEnvironmentForMainAPI(environment)}/api/instances/getInstances/${tenant}/${site}`
        return new FetchProxy(url, {
          credentials: "same-origin",
          mode: "cors",
          headers: {
            "AuthToken": token
          }
        })
        .withContainer(sdkContainerInstance)
        .fetch()
        .then(function (response) {
          if (!response.ok) {
            throw (response.status);
          } else {
            return response.json();
          }
        }).then(response => {
          console.log(response);
          resolve(response);
        }).catch((error) => {
          console.log(error);
        });
      } else {
        resolve(null);
      }
    });
  }

  public static signOut(url: string, token: string): Promise<void> {
    return new Promise((resolve) => {
      return new FetchProxy(url, {
        method: "DELETE",
        credentials: "same-origin",
        mode: "cors",
        headers: {
          "AuthToken": token,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      })
      .withContainer(sdkContainerInstance)
      .fetch()
      .then(function (response) {
        if (!response.ok) {
          throw (response.status);
        } else {
          resolve();
        }
      });
    });
  }
}

